/**
 * ESPN athlete payloads to our internal player record.
 *
 * Sits alongside `espnNormalizer` and follows the same two rules: the
 * vendor's field names stop here, and unknown means unknown. A field ESPN
 * did not send is `null`, never a guess and never a placeholder that reads
 * like a fact.
 *
 * THREE SHAPES IN, ONE SHAPE OUT
 * ------------------------------
 * ESPN describes the same human three different ways depending on which
 * endpoint you ask, and the differences are not cosmetic:
 *
 *   profile   `/apis/common/v3/.../athletes/{id}` nests the athlete under
 *             `athlete` and is the only shape carrying `statsSummary`,
 *             `displayDraft`, `displayExperience` and a `team` object.
 *   roster    `/apis/site/v2/.../teams/{abbr}/roster` groups athletes under
 *             `athletes[].items`, carries numeric `height`/`weight` and a
 *             structured `birthPlace`, and carries no season stats and no
 *             team object at all (only a `$ref` we do not follow).
 *   boxscore  the game package's `boxscore.players` carries the barest
 *             record: id, names, jersey, headshot. It is the only one of
 *             the three that is scoped to a single game, which is why the
 *             play-feed name index is built from it (see playerNameIndex).
 *
 * `normalizePlayer` reads whichever fields are present and leaves the rest
 * null, so one record shape covers all three and a UI never has to ask
 * which endpoint a player came from.
 *
 * Every mapping below was written against real captured responses in
 * `__fixtures__/`, not against documentation: this API has none.
 */

import { normalizeTeamId } from "./espnNormalizer";

/**
 * Where a player record came from, carried on the record itself.
 *
 * Games attribute themselves with `provider` plus `isSimulated`, which
 * `sourceForGame` in src/data/dataSources.js turns into a badge. Player
 * records carry the same two fields so that helper works on them unchanged,
 * plus an explicit `provenance` block, because a profile page has room to
 * say where the numbers came from and should.
 *
 * `source` is "espn" rather than "nfl": this is ESPN's player database,
 * not a league feed, and the badge copy already draws that distinction.
 */
const SOURCE_ID = "espn";
const SOURCE_NAME = "ESPN";
const ATTRIBUTION = "Player profiles and stats from ESPN's public data.";

export function playerProvenance({ endpoint = null, retrievedAt = null } = {}) {
  return Object.freeze({
    source: SOURCE_ID,
    sourceName: SOURCE_NAME,
    attribution: ATTRIBUTION,
    endpoint,
    retrievedAt,
  });
}

/**
 * ESPN's headshot CDN is addressed by athlete id alone, so a record can
 * always offer a portrait even when the payload omitted the field. The
 * payload's own href wins when it is there.
 */
export function headshotUrlFor(athleteId) {
  const id = String(athleteId ?? "").trim();
  if (!/^\d+$/.test(id)) return null;
  return `https://a.espncdn.com/i/headshots/nfl/players/full/${id}.png`;
}

/**
 * Generational suffixes, which ESPN keeps on the roster record ("Etienne
 * Jr.") and drops from play-by-play text ("T.Etienne"). Stripping them is
 * what lets the two be matched at all. It is also what makes Travis
 * Etienne Jr. and Trevor Etienne collide, which is a real ambiguity in
 * ESPN's own play text rather than something this code introduces.
 */
const NAME_SUFFIX = /\s+(?:Jr|Sr|II|III|IV|V)\.?$/i;

/**
 * ESPN LENGTHENS THE INITIAL WHEN ONE LETTER IS NOT ENOUGH
 * --------------------------------------------------------
 * The play feed does not always spell a player "X.Surname". When one
 * letter would name two people, ESPN writes more of the first name, and
 * it does so *precisely* in the cases where guessing would be dangerous:
 *
 *   Ty.Johnson / Ta.Johnson   Ty Johnson and Taron Johnson, both Buffalo
 *   Ja.Phillips / Jo.Phillips Jaelan and Jordan Phillips, both Miami
 *   Cas.Washington            Casey Washington, three letters
 *
 * Counted over the 2,914 plays of the 16 games of 2025 regular season
 * week 1, ESPN wrote 7,015 one-letter initials, 53 two-letter and 6
 * three-letter, and nothing longer. So a key of the one-letter form alone
 * cannot be found in the text at exactly the moments ESPN has already
 * done the disambiguation for us, and the reader is left with either no
 * face or, worse, the next name in the sentence.
 *
 * Hence keys, plural: every prefix of the first name from one letter up
 * to `MAX_INITIAL_LETTERS`. The extra keys cost nothing when ESPN does
 * not use them, because a key is only ever found by searching for it
 * verbatim in the text.
 *
 * Three is not an arbitrary cap, it is the longest form observed. If
 * ESPN ever writes a fourth letter the token simply will not be found,
 * which shows the reader plain text rather than the wrong person.
 */
const MAX_INITIAL_LETTERS = 3;

/**
 * How the play-by-play feed can spell this player, longest last.
 *
 * The surname is passed through verbatim on purpose. Real play text keeps
 * internal spaces and full stops ("A.St. Brown", "A.Van Ginkel"), keeps
 * hyphens and apostrophes ("J.Smith-Njigba", "H.To'oTo'o"), and drops only
 * the suffix. Anything cleverer than "strip the suffix" would stop the key
 * matching the text.
 *
 * The first name is treated the same way, and only its leading run of
 * plain letters is used. "Ja'Marr" yields "J." and "Ja." but never
 * "Ja'." , and "A.J." yields "A." alone, which is exactly how ESPN
 * writes both of them.
 *
 * Returns an empty array when either half is missing, because a
 * half-built key would match the wrong people.
 */
export function playerNameKeys(firstName, lastName) {
  const given = String(firstName ?? "").trim();
  const surname = String(lastName ?? "").trim().replace(NAME_SUFFIX, "").trim();
  const letters = (given.match(/^[A-Za-z]+/) ?? [""])[0];
  if (!letters || !surname) return [];

  const stem = letters.charAt(0).toUpperCase() + letters.slice(1);
  const longest = Math.min(stem.length, MAX_INITIAL_LETTERS);

  const keys = [];
  for (let length = 1; length <= longest; length += 1) {
    keys.push(`${stem.slice(0, length)}.${surname}`);
  }
  return keys;
}

/**
 * The one-letter spelling, which is how ESPN writes a player 99.2% of the
 * time and therefore the one to show a human. Null when unmatchable.
 */
export function playerNameKey(firstName, lastName) {
  return playerNameKeys(firstName, lastName)[0] ?? null;
}

/** "Patrick", "Mahomes" becomes "P. Mahomes", for tight spaces. */
function shortNameOf(firstName, lastName, displayName) {
  const initial = String(firstName ?? "").trim().charAt(0);
  const surname = String(lastName ?? "").trim();
  if (!initial || !surname) return displayName ?? null;
  return `${initial.toUpperCase()}. ${surname}`;
}

const text = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : trimmed;
};

const finiteOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * ESPN's `displayBirthPlace` is already a formatted string on the profile,
 * but the roster gives only the parts, so the two shapes have to be made
 * to agree.
 *
 * The joining rule is ESPN's own, read off real profiles rather than
 * assumed, and it is not the obvious "join everything":
 *
 *   Whitehouse, TX, USA        becomes "Whitehouse, TX"
 *   Sydney, NSW, Australia     becomes "Sydney, NSW"
 *   Abuja, Nigeria (no state)  becomes "Abuja, Nigeria"
 *
 * So: the city, then the state if there is one, and the country only when
 * there is no state. Note that this is not a rule about being American.
 * A player born in Sydney loses "Australia" for the same reason a player
 * born in Texas loses "USA": the state already places them.
 */
function birthPlaceTextOf(athlete) {
  const display = text(athlete.displayBirthPlace);
  if (display) return display;

  const place = athlete.birthPlace ?? {};
  const city = text(place.city);
  const region = text(place.state) ?? text(place.country);

  const parts = [city, region].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * Season stats with their league ranks, which is the "pro stats" half of a
 * profile. Only the profile endpoint carries this block.
 *
 * `rank` is kept separate from `rankDisplayValue` because the display form
 * carries information the number does not: ESPN writes "Tied-27th" when a
 * player shares a rank, and flattening that to "27th" would be a small lie.
 */
function normalizeSeasonStats(statsSummary) {
  const statistics = statsSummary?.statistics;
  if (!Array.isArray(statistics) || statistics.length === 0) return null;

  return {
    label: text(statsSummary.displayName),
    entries: statistics.map((stat) => ({
      key: text(stat.name),
      label: text(stat.displayName),
      shortLabel: text(stat.shortDisplayName),
      abbreviation: text(stat.abbreviation),
      description: text(stat.description),
      value: finiteOrNull(stat.value),
      displayValue: text(stat.displayValue),
      rank: finiteOrNull(stat.rank),
      rankText: text(stat.rankDisplayValue),
    })),
  };
}

/**
 * The current injury, if ESPN is reporting one.
 *
 * Only the first entry is used: the list is ordered newest first on every
 * real payload we captured, and a beginner reading a profile wants the
 * current status, not an injury history.
 */
function normalizeInjury(injuries) {
  const injury = Array.isArray(injuries) ? injuries[0] : null;
  if (!injury) return null;

  const statusText = text(injury.status) ?? text(injury.type?.description);
  if (!statusText) return null;

  const details = injury.details ?? {};
  return {
    statusText,
    abbreviation: text(injury.type?.abbreviation),
    type: text(details.type),
    location: text(details.location),
    side: text(details.side),
    detail: text(details.detail),
    reportedAt: text(injury.date),
    expectedReturnAt: text(details.returnDate),
  };
}

/**
 * One athlete, from any of the three shapes.
 *
 * @param {object} athlete raw ESPN athlete
 * @param {{ teamId?: string|null, provenance?: object, rosterGroup?: string|null }} [context]
 *   `teamId` is supplied by the caller when the athlete record itself does
 *   not carry a team, which is the case for both the roster and the
 *   boxscore shapes.
 */
export function normalizePlayer(athlete, context = {}) {
  const providerPlayerId = text(athlete?.id);
  if (!providerPlayerId) return null;

  const firstName = text(athlete.firstName);
  const lastName = text(athlete.lastName);
  const displayName =
    text(athlete.displayName) ??
    text(athlete.fullName) ??
    text([firstName, lastName].filter(Boolean).join(" "));

  // The team object only exists on the profile shape; otherwise the caller
  // knows which team it asked about.
  const teamId =
    normalizeTeamId(athlete.team?.abbreviation) ?? context.teamId ?? null;

  const position = athlete.position ?? {};
  const status = athlete.status ?? {};

  return {
    playerId: `nfl-player-${providerPlayerId}`,
    providerPlayerId,
    provider: "nfl",
    isSimulated: false,

    firstName,
    lastName,
    fullName: text(athlete.fullName) ?? displayName,
    displayName,
    shortName: text(athlete.shortName) ?? shortNameOf(firstName, lastName, displayName),
    /** How the play-by-play feed spells this player. Null when unmatchable. */
    nameKey: playerNameKey(firstName, lastName),
    /**
     * Every spelling the feed may use, including the lengthened initials
     * ESPN falls back on when one letter would name two people. Always
     * starts with `nameKey`, and is empty exactly when `nameKey` is null.
     */
    nameKeys: playerNameKeys(firstName, lastName),

    jersey: text(athlete.jersey),
    jerseyText: text(athlete.displayJersey) ?? (text(athlete.jersey) ? `#${text(athlete.jersey)}` : null),
    position: text(position.abbreviation),
    positionName: text(position.displayName) ?? text(position.name),
    teamId,
    /** Which roster block ESPN filed them under, e.g. "injuredReserveOrOut". */
    rosterGroup: text(context.rosterGroup),

    headshotUrl: text(athlete.headshot?.href) ?? headshotUrlFor(providerPlayerId),

    // `active` is only on the profile shape. Absent is unknown, not false.
    isActive: typeof athlete.active === "boolean" ? athlete.active : null,
    statusText: text(status.name),

    college: text(athlete.college?.name) ?? text(athlete.college?.shortName),
    heightText: text(athlete.displayHeight),
    weightText: text(athlete.displayWeight),
    // Numeric height and weight are roster-only. Never parsed out of the
    // display strings, which are locale-formatted.
    heightInches: finiteOrNull(athlete.height),
    weightPounds: finiteOrNull(athlete.weight),
    age: finiteOrNull(athlete.age),
    birthPlaceText: birthPlaceTextOf(athlete),
    // Passed through as an opaque string. ESPN returns `displayDOB` already
    // formatted, and the field order it uses is not documented, so parsing
    // it into a Date risks turning 9 December into 12 September.
    birthDateText: text(athlete.displayDOB),
    birthDateIso: text(athlete.dateOfBirth),

    draftText: text(athlete.displayDraft),
    experienceText: text(athlete.displayExperience),
    experienceYears: finiteOrNull(athlete.experience?.years),
    debutYear: finiteOrNull(athlete.debutYear),

    injury: normalizeInjury(athlete.injuries),
    seasonStats: normalizeSeasonStats(athlete.statsSummary),

    provenance: context.provenance ?? playerProvenance(),
  };
}

/**
 * The profile endpoint's payload.
 * Returns null rather than a hollow record when there is no athlete in it.
 */
export function normalizePlayerProfile(payload, context = {}) {
  const athlete = payload?.athlete ?? null;
  if (!athlete) return null;
  return normalizePlayer(athlete, context);
}

/**
 * The roster endpoint's payload.
 *
 * ESPN groups athletes as offense / defense / specialTeam /
 * injuredReserveOrOut / suspended / practiceSquad. The group is worth
 * keeping: "on injured reserve" is exactly the sort of thing a beginner
 * wonders about when a name they know is missing from the field.
 */
export function normalizeRoster(payload, context = {}) {
  const teamId =
    normalizeTeamId(payload?.team?.abbreviation) ?? context.teamId ?? null;

  const players = (payload?.athletes ?? []).flatMap((group) =>
    (group?.items ?? [])
      .map((athlete) =>
        normalizePlayer(athlete, {
          ...context,
          teamId,
          rosterGroup: group?.position ?? null,
        })
      )
      .filter(Boolean)
  );

  return {
    teamId,
    provider: "nfl",
    isSimulated: false,
    season: finiteOrNull(payload?.season?.year),
    seasonName: text(payload?.season?.name) ?? text(payload?.season?.displayName),
    retrievedAt: text(payload?.timestamp),
    players,
    provenance: context.provenance ?? playerProvenance(),
  };
}

/**
 * Every athlete named in a game package's boxscore, deduplicated.
 *
 * An athlete appears once per statistical category they recorded something
 * in, so the raw list has more rows than people. The first row wins; they
 * are the same athlete object each time.
 *
 * The team comes from the boxscore grouping rather than the athlete, which
 * is the only place it exists in this shape.
 */
export function normalizeBoxscorePlayers(gamePackage, context = {}) {
  const teams = gamePackage?.boxscore?.players ?? [];
  const byId = new Map();

  teams.forEach((entry) => {
    const teamId = normalizeTeamId(entry?.team?.abbreviation);

    (entry?.statistics ?? []).forEach((group) => {
      (group?.athletes ?? []).forEach((row) => {
        const player = normalizePlayer(row?.athlete, { ...context, teamId });
        if (!player || byId.has(player.providerPlayerId)) return;
        byId.set(player.providerPlayerId, player);
      });
    });
  });

  return [...byId.values()];
}

/**
 * A player's stat line for one game, read off the boxscore.
 *
 * ESPN gives a category's column headings in `labels` and each athlete's
 * figures in a positionally aligned `stats` array. Zipping them is safe
 * only while the two are the same length, so a mismatch drops the category
 * rather than pairing a number with the wrong heading.
 */
export function normalizeBoxscoreStatLines(gamePackage, athleteId) {
  const wanted = String(athleteId ?? "").trim();
  if (!wanted) return [];

  const lines = [];

  (gamePackage?.boxscore?.players ?? []).forEach((entry) => {
    (entry?.statistics ?? []).forEach((group) => {
      const labels = group?.labels ?? [];
      const keys = group?.keys ?? [];

      (group?.athletes ?? []).forEach((row) => {
        if (String(row?.athlete?.id ?? "") !== wanted) return;

        const stats = row?.stats ?? [];
        if (stats.length === 0 || stats.length !== labels.length) return;

        lines.push({
          category: text(group.name),
          entries: labels.map((label, i) => ({
            key: text(keys[i]),
            label: text(label),
            displayValue: text(stats[i]),
          })),
        });
      });
    });
  });

  return lines;
}

export const __testing = {
  MAX_INITIAL_LETTERS,
  NAME_SUFFIX,
  SOURCE_ID,
  shortNameOf,
};
